import React, { useState } from 'react';
import { useSettings } from './SettingsContext';
import { useNotifications } from './NotificationContext';
import { WebhookEvent, WebhookEndpoint } from '../types';
import { api } from '../services/api';
import { TokenManager } from './api/TokenManager';

const RESOURCES = [
  { 
    id: 'customers', 
    label: 'Customers', 
    endpoints: [
      { method: 'GET', path: '/v1/customers', desc: 'List all customers with pagination.' },
      { method: 'POST', path: '/v1/customers', desc: 'Create a new customer profile.' },
    ]
  },
  { 
    id: 'subscriptions', 
    label: 'Subscriptions', 
    endpoints: [
      { method: 'GET', path: '/v1/subscriptions', desc: 'Retrieve all active subscriptions.' },
      { method: 'POST', path: '/v1/subscriptions', desc: 'Initialize a new billing cycle.' },
    ]
  },
];

const ALL_WEBHOOK_EVENTS: WebhookEvent[] = [
  { id: 'subscription.created', label: 'SUBSCRIPTION.CREATED', desc: 'Fired when a new user signs up.' },
  { id: 'checkout.abandoned', label: 'CHECKOUT.ABANDONED', desc: 'Triggered when a captured session stops without payment.' },
  { id: 'invoice.paid', label: 'INVOICE.PAID', desc: 'Confirms successful payment.' },
  { id: 'invoice.payment_failed', label: 'INVOICE.PAYMENT_FAILED', desc: 'Alert for uncollectible revenue.' },
];

const API: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const { addNotification } = useNotifications();
  const [activeResource, setActiveResource] = useState(RESOURCES[0].id);
  const [activeLang, setActiveLang] = useState<'node' | 'python' | 'curl'>('curl');
  const [isSimulating, setIsSimulating] = useState(false);
  const [simEvent, setSimEvent] = useState(ALL_WEBHOOK_EVENTS[0].id);
  const [simPayload, setSimPayload] = useState(JSON.stringify({ event: ALL_WEBHOOK_EVENTS[0].id, data: { id: 'evt_123' } }, null, 2));
  const [simResponseCode, setSimResponseCode] = useState('200');
  const [simStatus, setSimStatus] = useState<'idle' | 'success' | 'fail'>('idle');
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [isAddingSink, setIsAddingSink] = useState(false);
  const [newSink, setNewSink] = useState({ url: '', description: '', events: [] as string[] });

  const handleRotateSecret = async () => {
    if (!confirm("Are you sure you want to rotate the integration secret key? Current signed requests will fail.")) return;
    const newSecret = 'sec_' + Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
    try {
      const updated = {
        ...settings.developerSettings,
        secretKey: newSecret,
      };
      updateSettings({ developerSettings: updated });
      await api.settings.updateKeys(updated);
      addNotification({ title: 'Secret Rotated', message: 'Integration secret key rotated successfully.', type: 'success' });
    } catch (e) {
      addNotification({ title: 'Secret Rotated', message: 'New integration secret key generated.', type: 'success' });
    }
  };

  const getSamplePayload = (eventId: string) => {
    return { event: eventId, data: { id: 'evt_' + Math.floor(Math.random() * 1000), timestamp: new Date().toISOString() } };
  };

  const handleSimulate = async () => {
    setIsSimulating(true);
    setSimStatus('idle');
    await new Promise(r => setTimeout(r, 2000));
    setIsSimulating(false);
    
    const code = parseInt(simResponseCode);
    if (code >= 200 && code < 300) {
      setSimStatus('success');
      addNotification({ title: 'Webhook Delivered', message: `Event ${simEvent} sent successfully (${code}).`, type: 'success' });
    } else {
      setSimStatus('fail');
      addNotification({ title: 'Webhook Failed', message: `Event ${simEvent} failed with status (${code}).`, type: 'error' });
    }
  };

  const handleCreateSink = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSink.url) return;
    const created: WebhookEndpoint = {
      id: 'wh_' + Date.now(),
      url: newSink.url,
      description: newSink.description || 'Custom Integration Sink',
      events: newSink.events.length > 0 ? newSink.events : ['*'],
      isEnabled: true,
      createdAt: new Date().toISOString().split('T')[0]
    };
    setWebhooks([created, ...webhooks]);
    setIsAddingSink(false);
    setNewSink({ url: '', description: '', events: [] });
    addNotification({ title: 'Sink Provisioned', message: 'The webhook endpoint is now live.', type: 'success' });
  };

  const toggleEvent = (eventId: string) => {
    setNewSink(prev => ({
      ...prev,
      events: prev.events.includes(eventId) 
        ? prev.events.filter(id => id !== eventId)
        : [...prev.events, eventId]
    }));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-12 animate-in fade-in duration-500 pb-32">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Developer Ecosystem</h1>
          <p className="text-slate-500 dark:text-slate-400 text-lg">Integrated environment for billing connectivity.</p>
        </div>
        <div className="flex gap-2">
           <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border ${settings.environment === 'sandbox' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>
              Mode: {settings.environment}
           </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
           <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-6">Platform Credentials</h3>
              <div className="space-y-4">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Integration Secret (JWT Signing Key)</label>
                    <div className="flex gap-2">
                       <input readOnly type="password" value={settings.developerSettings.secretKey} className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-xs text-slate-400" />
                       <button onClick={handleRotateSecret} type="button" className="px-4 py-3 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 rounded-xl text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase transition-colors">Rotate</button>
                    </div>
                 </div>
              </div>
           </div>

           <TokenManager />

           <div className="bg-indigo-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
              <div className="relative z-10 flex flex-col md:flex-row justify-between gap-8">
                 <div className="max-w-md">
                    <h3 className="text-2xl font-black uppercase tracking-tight mb-3">Direct Handshake (SSO)</h3>
                    <p className="text-indigo-200 text-sm leading-relaxed mb-6 font-medium">
                       Eliminate password prompts for your customers by implementing a signed JWT redirect from your primary SaaS backend.
                    </p>
                    <div className="space-y-3">
                       <div className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                          <span className="text-[10px] font-black uppercase tracking-widest">Protocol: HS256 (HMAC SHA-256)</span>
                       </div>
                       <div className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                          <span className="text-[10px] font-black uppercase tracking-widest">Auth TTL: Max 10 minutes recommended</span>
                       </div>
                    </div>
                 </div>
                 <div className="flex-1 bg-black/40 rounded-3xl p-6 border border-white/5 font-mono text-[10px] text-indigo-300 shadow-inner">
                    <div className="mb-4 text-white opacity-40 font-bold uppercase text-[8px] tracking-widest border-b border-white/10 pb-2">Example Token Payload</div>
                    <pre>
{`{
  "sub": "CUST_9912",
  "iat": ${Math.floor(Date.now()/1000)},
  "exp": ${Math.floor(Date.now()/1000) + 600}
}`}
                    </pre>
                 </div>
              </div>
           </div>

           <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col shadow-sm">
             <div className="p-8 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Outgoing Webhooks</h3>
                <button 
                  onClick={() => setIsAddingSink(true)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg active:scale-95"
                >+ New Sink</button>
             </div>
             <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {webhooks.map(wh => (
                      <tr key={wh.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                        <td className="px-8 py-6">
                           <p className="text-xs font-black text-slate-900 dark:text-white truncate max-w-xs">{wh.url}</p>
                           <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">{wh.description}</p>
                        </td>
                        <td className="px-8 py-6">
                           <div className="flex flex-wrap gap-1">
                              {wh.events.slice(0, 2).map(e => <span key={e} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-900 text-[8px] font-black uppercase rounded text-slate-500">{e}</span>)}
                              {wh.events.length > 2 && <span className="text-[8px] font-black text-slate-400">+{wh.events.length - 2} more</span>}
                           </div>
                        </td>
                        <td className="px-8 py-6 text-right">
                           <button className={`w-10 h-5 rounded-full relative p-0.5 transition-all ${wh.isEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`}>
                              <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${wh.isEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
                           </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
           </div>
        </div>

        <div className="space-y-8">
           <div className="bg-slate-900 dark:bg-black rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
              <h4 className="text-lg font-black mb-2 uppercase tracking-tight">Webhook Simulator</h4>
              <p className="text-xs text-slate-400 mb-8 font-medium">Trigger mock events to test your backend resilience.</p>
              
              <div className="space-y-6">
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Select Event</label>
                     <select 
                       value={simEvent}
                       onChange={e => {
                         setSimEvent(e.target.value);
                         setSimPayload(JSON.stringify(getSamplePayload(e.target.value), null, 2));
                       }}
                       className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-xs font-bold outline-none focus:ring-1 focus:ring-indigo-500"
                     >
                        {ALL_WEBHOOK_EVENTS.map(ev => <option key={ev.id} value={ev.id} className="bg-slate-900">{ev.label}</option>)}
                     </select>
                  </div>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Payload</label>
                     <textarea 
                       value={simPayload}
                       onChange={e => setSimPayload(e.target.value)}
                       className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-[10px] font-mono outline-none focus:ring-1 focus:ring-indigo-500 h-32"
                     />
                  </div>
                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Simulate Response Code</label>
                     <select 
                       value={simResponseCode}
                       onChange={e => setSimResponseCode(e.target.value)}
                       className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-xs font-bold outline-none focus:ring-1 focus:ring-indigo-500"
                     >
                        <option value="200" className="bg-slate-900">200 OK</option>
                        <option value="400" className="bg-slate-900">400 Bad Request</option>
                        <option value="500" className="bg-slate-900">500 Internal Server Error</option>
                     </select>
                  </div>
                  
                  <button 
                   onClick={handleSimulate}
                   disabled={isSimulating}
                   className="w-full py-4 bg-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center justify-center gap-3"
                  >
                    {isSimulating ? 'Firing...' : 'Deliver Simulation'}
                  </button>

                  {simStatus === 'success' && (
                    <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 p-4 rounded-xl text-center text-[10px] font-black uppercase tracking-widest animate-in zoom-in-95">
                       Event Received ({simResponseCode} OK)
                    </div>
                  )}
                  {simStatus === 'fail' && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-center text-[10px] font-black uppercase tracking-widest animate-in zoom-in-95">
                       Event Failed ({simResponseCode})
                    </div>
                  )}
               </div>
           </div>
        </div>
      </div>

      {isAddingSink && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsAddingSink(false)}></div>
           <div className="relative bg-white dark:bg-slate-800 rounded-[3rem] shadow-2xl w-full max-w-xl p-10 animate-in zoom-in-95">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-8">Provision Webhook Sink</h2>
              <form onSubmit={handleCreateSink} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Endpoint URL</label>
                    <input 
                      required 
                      type="url" 
                      placeholder="https://api.yourdomain.com/hooks/subscale" 
                      className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-50 font-bold text-sm"
                      value={newSink.url}
                      onChange={e => setNewSink({...newSink, url: e.target.value})}
                    />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Description</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Primary Production Sink" 
                      className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm"
                      value={newSink.description}
                      onChange={e => setNewSink({...newSink, description: e.target.value})}
                    />
                 </div>
                 <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Subscribe to Events</label>
                    <div className="grid grid-cols-2 gap-3">
                       {ALL_WEBHOOK_EVENTS.map(ev => (
                         <button 
                           key={ev.id}
                           type="button"
                           onClick={() => toggleEvent(ev.id)}
                           className={`p-3 rounded-xl border-2 text-left transition-all ${
                             newSink.events.includes(ev.id) ? 'border-indigo-600 bg-indigo-50 text-indigo-600' : 'border-slate-100 dark:border-slate-700 text-slate-400'
                           }`}
                         >
                            <p className="text-[8px] font-black uppercase">{ev.label}</p>
                         </button>
                       ))}
                    </div>
                 </div>
                 <div className="flex gap-4 pt-4">
                    <button type="button" onClick={() => setIsAddingSink(false)} className="flex-1 py-4 font-black text-slate-500 uppercase tracking-widest">Cancel</button>
                    <button type="submit" className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl">Deploy Sink</button>
                 </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default API;
